import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Container, Row, Col, Card, CardBody, Button, Spinner, Alert
} from "react-bootstrap";
import { Clock, ArrowRight, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import usePaymentStatus from "../../apis/PaymentStatusApi";
import AppLayout from "../../layouts/AppLayout";

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [lastStatus, setLastStatus] = useState(null);
  const [lastCheckedTime, setLastCheckedTime] = useState(null);

  const state = location.state || {};
  const orderId = state.orderId || state.order_id;

  const { getPaymentStatus, paymentData, isLoading, error } = usePaymentStatus();

  // ✅ INITIAL LOAD
  useEffect(() => {
    console.log("═══════════════════════════════════════");
    console.log("📄 [Checkout] Page loaded");
    console.log("   Order ID:", orderId);
    console.log("═══════════════════════════════════════");
  }, [orderId]);

  // ✅ UPDATE STATUS FROM REDUX
  useEffect(() => {
    if (paymentData?.payment_status) {
      console.log("🔄 [Checkout] Status update:", paymentData.payment_status);
      setLastStatus(paymentData.payment_status);
      setLastCheckedTime(new Date());
    }
  }, [paymentData]);

  // ✅ AUTO-REDIRECT ON COMPLETION
  useEffect(() => {
    if (paymentData && paymentData.payment_status !== "pending") {
      console.log("✅ [Checkout] Payment completed - Redirecting");
      navigate("/PaymentReceipt", { 
        state: { 
          orderId: orderId, 
          paymentData: paymentData 
        },
        replace: true
      });
    }
  }, [paymentData, navigate, orderId]);

  // ✅ REFRESH STATUS
  const handleRefreshStatus = () => {
    if (!orderId) return;

    console.log("🔄 [Checkout] Refreshing status");
    
    getPaymentStatus(orderId, (resp) => {
      console.log("📨 [Checkout] Status response:", resp);
      
      if (resp?.is_success && resp.data) {
        setLastStatus(resp.data.payment_status);
        setLastCheckedTime(new Date());
        
        if (resp.data.payment_status !== "pending") {
          navigate("/PaymentReceipt", { 
            state: { 
              orderId: orderId, 
              paymentData: resp.data 
            },
            replace: true
          });
        }
      }
    });
  };

  // ✅ NO ORDER ID
  if (!orderId) {
    return (
      <AppLayout>
        <Container className="py-5 text-center">
          <Alert variant="warning">
            <Warning size={24} className="me-2" />
            No payment data found
          </Alert>
          <Button onClick={() => navigate("/dashboard/")}>
            Go to Dashboard
          </Button>
        </Container>
      </AppLayout>
    );
  }

  const isPending = lastStatus === "pending";
  const isSuccess = lastStatus === "success" || lastStatus === "completed";
  const isFailed = lastStatus === "failed" || lastStatus === "cancelled";

  return (
    <AppLayout>
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs={12} md={6}>
            <Card className="shadow-lg border-0 text-center">
              <CardBody className="p-5">
                
                {/* ICON */}
                <div className="mb-4">
                  <Clock size={80} weight="duotone" className="text-primary mb-3" />
                  {isPending && <Spinner animation="border" variant="primary" />}
                </div>

                {/* TITLE */}
                <h3 className="mb-3">Payment Status</h3>
                <p className="text-muted mb-4">
                  {isPending ? "Processing your payment..." : "Check your payment status"}
                </p>

                {/* ORDER ID */}
                <Alert variant="light" className="mb-3">
                  <small className="text-muted d-block mb-1">Order ID</small>
                  <strong className="fs-5">{orderId}</strong>
                </Alert>

                {/* STATUS ALERTS */}
                {isPending && (
                  <Alert variant="warning" className="mb-3">
                    ⏳ Payment Not Completed
                  </Alert>
                )}

                {isSuccess && (
                  <Alert variant="success" className="mb-3">
                    ✅ Payment Successful!
                  </Alert>
                )}

                {isFailed && (
                  <Alert variant="danger" className="mb-3">
                    ❌ Payment Failed
                  </Alert>
                )}

                {/* LOADING */}
                {isLoading && (
                  <Alert variant="info" className="mb-3">
                    <Spinner size="sm" className="me-2" />
                    Checking payment status...
                  </Alert>
                )}

                {/* ERROR */}
                {error && (
                  <Alert variant="danger" className="mb-3">
                    <Warning size={16} className="me-2" />
                    {error}
                  </Alert>
                )}

                {/* LAST CHECKED */}
                {lastCheckedTime && (
                  <div className="mb-4">
                    <p className="text-muted small mb-1">Last checked</p>
                    <div className="text-secondary">
                      {lastCheckedTime.toLocaleTimeString('en-IN')}
                    </div>
                  </div>
                )}

                {/* BUTTONS */}
                <div className="d-grid gap-2">
                  <Button 
                    variant="success" 
                    size="lg"
                    onClick={handleRefreshStatus}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <ArrowsClockwise size={20} className="me-2" weight="bold" />
                        Refresh Status
                      </>
                    )}
                  </Button>

                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={() => navigate("/PaymentReceipt", { 
                      state: { orderId: orderId } 
                    })}
                  >
                    View Receipt
                    <ArrowRight size={20} className="ms-2" />
                  </Button>

                  <Button 
                    variant="outline-secondary"
                    onClick={() => navigate("/dashboard/")}
                  >
                    Back to Dashboard
                  </Button>
                </div>

              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </AppLayout>
  );
};

export default Checkout;
